import React, { useState, useEffect, useRef } from 'react'
import { round, vn } from 'vend-number'
import { trackError } from '@vend/utilities'
import { PAYMENT_TYPES } from '@vend/business-components'
import {
  IntegratedPaymentModal,
  PaymentData,
} from '../../../payments/integrated-payment/integrated-payment-modal/IntegratedPaymentModal'
import {
  AcceptStep,
  RejectStep,
} from '../../../payments/integrated-payment/integrated-payment-steps.constant'
import { useAppSelector } from '../../../../state/hooks'
import {
  selectCurrentSale,
  selectOutstandingBalance,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import {
  useRegisterManager,
  useRetailerSettingsManager,
  useSaleManager,
  useTrainingModeManager,
} from '../../../../ng-compat/hooks'
import { env } from '../../../../../../lib/env'
import { formatToClientApi } from '../../../../functions/sale-transformer'
import { lineItemsAdapter } from '../../../../state/slices/currentSale/lineItemEntityAdapter'
import {
  useHandleCompleteIntegratedPayment,
  useHandleRejectedIntegratedPayment,
} from '../hooks/useHandleIntegrationPayment'
import { PartialAuthModal } from '../../../payments/partial-auth-modal/PartialAuthModal'
import { IntegratedPaymentOption, SalePayment } from './types'

interface Props {
  tenderAmount: string
  paymentOptions: IntegratedPaymentOption
  onSuccess: (result: SalePayment) => Promise<void>
  onClose: () => void
}

export const IntegratedPayment = ({
  paymentOptions,
  tenderAmount,
  onSuccess,
  onClose,
}: Props) => {
  const sale = useAppSelector(selectCurrentSale)
  const outstandingBalance = useAppSelector(selectOutstandingBalance)
  const saleManager = useSaleManager()
  const trainingModeManager = useTrainingModeManager()
  const { handleRejectedPayment } = useHandleRejectedIntegratedPayment()
  const outlet = useRegisterManager().getActiveOutlet()
  const discountProductId = useRetailerSettingsManager().get()
    .discount_product_id
  const [paymentData, setPaymentData] = useState<PaymentData>({
    currency: outlet.currency,
    retailerPaymentTypeId: '',
    amount: round(paymentOptions.amount, 2), //! For some integrated payment providers (SmartPay), it is important to have two digits behind the decimal points
    paymentType: paymentOptions.paymentType,
    registerId: paymentOptions.registerId,
    externalTransactionIds: paymentOptions.externalTransactionIds ?? [],
    registerSalePaymentIds: paymentOptions.registerSalePaymentIds ?? [],
    paymentId: paymentOptions.paymentId,
  })
  const [partialPayment, setPartialPayment] = useState<AcceptStep>()

  const {
    handleAcceptedPayment,
    handlePartialAuthRejected,
  } = useHandleCompleteIntegratedPayment(
    onSuccess,
    setPartialPayment,
    setPaymentData,
    tenderAmount
  )

  const hasLoggedOverpaymentRef = useRef(false)
  const initialBalanceRef = useRef(vn(outstandingBalance).toNumber())
  const isPaymentSucceedingRef = useRef(false)

  // Track how long the modal has been open
  const mountTimeRef = useRef(Date.now())

  useEffect(
    function logWindcaveOverpayment() {
      if (hasLoggedOverpaymentRef.current) return
      if (!env.isProduction()) return
      if (paymentOptions.paymentType.type_id !== PAYMENT_TYPES.WINDCAVE_VEND)
        return
      if (trainingModeManager.trainingModeEnabled) return
      if (vn(outstandingBalance).lte(0)) return

      if (isPaymentSucceedingRef.current) return
      const balanceDropped = vn(initialBalanceRef.current).gt(
        outstandingBalance
      )

      const overpaymentDelta = vn(paymentOptions.amount).minus(
        vn(outstandingBalance)
      )

      if (balanceDropped && overpaymentDelta.gt(0.5)) {
        hasLoggedOverpaymentRef.current = true
        trackError(
          '[WindcaveOverpayment] Payment amount exceeds recalculated sale total',
          {
            data: {
              paymentAmount: paymentOptions.amount,
              initialBalance: initialBalanceRef.current,
              currentOutstandingBalance: outstandingBalance,
              overpaymentDelta: overpaymentDelta.toNumber(),
              paymentTypeName: paymentOptions.paymentType.name,
              retailerPaymentTypeId: paymentOptions.paymentType.id,
              registerId: paymentOptions.registerId,
              outletId: outlet.id,
              saleId: sale.id,
              calculationsPendingAtTender: saleManager.getPendingSaleCalculations(),
              saleCustomerId: sale.customer?.id ?? '',
              timeSinceModalOpenMs: Date.now() - mountTimeRef.current,
            },
          },
          ['windcave_overpayment_detected']
        )
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- outstandingBalance is the only dep that changes (Redux updates post-recalculation). paymentOptions.amount is intentionally frozen at button-click time; saleManager and trainingModeManager are stable references.
    [outstandingBalance]
  )

  const saleData = formatToClientApi(
    sale,
    lineItemsAdapter.getSelectors().selectAll(sale.lineItems),
    discountProductId
  )

  const handleClose = (val?: Promise<AcceptStep>) => {
    if (val) {
      isPaymentSucceedingRef.current = true
      val
        .then(step => {
          handleAcceptedPayment(paymentOptions.paymentType, tenderAmount, step)
        })
        .catch((rejection: RejectStep) => {
          isPaymentSucceedingRef.current = false
          handleRejectedPayment(paymentOptions.paymentType, rejection)
          onClose()
        })
    }
  }

  const handlePartialAuthClose = (applyPartialAuth: Promise<boolean>) => {
    applyPartialAuth.then(allowPartialAuth => {
      if (allowPartialAuth) {
        // A partial payment is being accepted, which will drop the balance. Ignore it.
        isPaymentSucceedingRef.current = true
      } else if (partialPayment) {
        handlePartialAuthRejected(paymentOptions.paymentType, partialPayment)
        return
      }
      onClose()
    })
  }

  if (partialPayment) {
    return (
      <PartialAuthModal
        close={handlePartialAuthClose}
        approvedAmount={
          partialPayment.approved_amount
            ? vn(partialPayment.approved_amount).toNumber()
            : vn(tenderAmount).toNumber()
        }
      />
    )
  }

  return (
    <IntegratedPaymentModal
      saleData={saleData}
      paymentData={paymentData}
      close={handleClose}
    />
  )
}
